/**
 * FixedSlotGlyphRow (S4, CONSULTS §2) — the detail pane's fixed-slot
 * baseline glyph row. IDENTICAL slot order on every recipe; the row never
 * gets shorter (a shorter row is impossible by construction).
 *
 * Presence semantics:
 * - present            → full-contrast glyph + label (teal = structured).
 * - absent-by-design   → struck label with an em-dash marker at low
 *   contrast (dull roar) — NEVER plain grey: grey is reserved disabled
 *   semantics and would read "unlockable"; the absence is BY DESIGN.
 *
 * An optional secondary count ("4 of 7") may sit beside the row — it never
 * replaces the glyphs.
 */

import React from 'react';
import { GlyphSlot } from './shapeFloor';
import { GALLERY_STRINGS } from '../galleryStrings';
import { cn } from '@/lib/utils';

/** THE fixed slot order — identical on every recipe, never reordered. */
export const FIXED_SLOT_ORDER: readonly GlyphSlot[] = [
  'toc',
  'keyTakeaways',
  'list',
  'table',
  'callout',
  'image',
  'faq',
] as const;

export interface GlyphSlotRowProps {
  /** Effective presence per slot (S1 clamped glyphs: config OR floor). */
  glyphs: Record<GlyphSlot, boolean>;
  /** Show the optional secondary count beside the row (never replacing it). */
  showCount?: boolean;
  className?: string;
}

export const GlyphSlotRow: React.FC<GlyphSlotRowProps> = ({
  glyphs,
  showCount = true,
  className,
}) => {
  const presentCount = FIXED_SLOT_ORDER.filter((slot) => glyphs[slot]).length;
  return (
    <div
      data-testid="glyph-slot-row"
      className={cn('flex items-center gap-2 flex-wrap', className)}
    >
      {FIXED_SLOT_ORDER.map((slot) => {
        const present = glyphs[slot];
        return (
          <span
            key={slot}
            data-glyph-slot={slot}
            data-slot-state={present ? 'present' : 'absent'}
            aria-label={`${GALLERY_STRINGS.pane.slots[slot]}: ${present ? 'included' : 'not included by design'}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
              present
                ? 'bg-teal-light text-teal-dark'
                : 'text-ink-3 line-through decoration-ink-3',
            )}
          >
            {!present && <span aria-hidden="true">—</span>}
            {GALLERY_STRINGS.pane.slots[slot]}
          </span>
        );
      })}
      {showCount && (
        <span
          data-slot-count="true"
          className="text-[10px] tabular-nums text-muted-foreground"
        >
          {presentCount} of {FIXED_SLOT_ORDER.length}
        </span>
      )}
    </div>
  );
};
